import { SharedAppSettings } from './shared-app-settings';

/** Query-param prefix that marks a setting override, e.g. `?lh_ft_bookingWizard=false`. */
export const URL_SETTING_OVERRIDE_PREFIX = 'lh_ft_';

/** Query param that drops every active override for the session, e.g. `?lh_ft_clear=1`. */
export const URL_SETTING_OVERRIDE_CLEAR_PARAM = `${URL_SETTING_OVERRIDE_PREFIX}clear`;

/**
 * The settings a URL param is allowed to override (AM-4658).
 *
 * An allow-list rather than "anything in settings" on purpose: this is a URL-addressable surface, so
 * an open list turns every future setting into a remote control for whoever crafts the link. Adding a
 * setting here is a deliberate act — the bar is "a presentation choice I'd want to demo or reproduce",
 * not "a setting that exists".
 *
 * Four kinds of setting are deliberately absent:
 *
 *   - **Operational data**, e.g. `storageLocationsUsingOldGuestDropoffFlow` and the PIN reuse/refresh
 *     timings. Those select real behaviour per shop, not a presentation; forcing them from a link is
 *     how a demo turns into a support incident.
 *   - **Free-text URLs**, i.e. `trustpilotReviewUrl`. It is the one value where a crafted link changes
 *     where a traveler's *click* goes rather than what they see — a phishing primitive, and the "no
 *     capability devtools doesn't already give" argument doesn't cover what a link does to *someone
 *     else's* session.
 *   - **`urlSettingOverridesEnabled` itself** — see {@link URL_SETTING_OVERRIDES_ENABLED_SETTING}.
 *   - **Switches the API enforces too**, i.e. `isBookingCheckInEnabled` / `isBookingCheckOutEnabled`
 *     (AM-4874). dashboard-api reads the same `appsettings` row and answers 404 while it is off, so a
 *     URL override could only ever show a button that does not work — the one state those switches
 *     exist to rule out. Flip the row in the environment instead.
 *
 * **Keep these names in step with `SharedAppSettings`.** `SharedAppSettings` carries an index
 * signature, so `keyof` gives `string` and a stale key cannot be caught by the compiler: renaming a
 * setting elsewhere leaves a dead entry here that accepts a param and writes a value nothing reads —
 * a silent no-op. AM-4661 renaming `showAccumulatedStorageRate` to
 * `bookingWizardAccumulatedStorageRate` did exactly that, and merged without a git conflict.
 *
 * Each entry **declares its type**, which is a deliberate step beyond the ticket's "coerce to the type
 * of the value already there". Reading the type off the current value works only when the setting *has*
 * one: `serviceFeeFootnoteCities` has no app default and is often unset server-side, so there was
 * nothing to coerce against and a comma list silently became the string `'a,b'`. `FeatureResolver` then
 * calls `.includes(officialLocationKey)` on it, which on a string is a *substring* test — so
 * `'copenhagen'.includes('open')` is true where `['copenhagen'].includes('open')` is false, and the
 * override resolves to the opposite answer with no warning. Declaring the type removes the guess.
 */
/**
 * The value shapes a URL param can express for an allow-listed setting.
 *
 * `{ oneOf }` is for settings whose real type is a closed union: declaring plain `'string'` for one
 * accepts any near-miss (`post_validation` for `post-validation`), which consumers then compare by
 * equality — so the app silently behaves as the unset default while the badge insists the override is
 * in force. Same silent-wrong-effect class the boolean case rejects `off` to avoid.
 */
export type OverridableSettingType =
  | 'boolean'
  | 'number'
  | 'string'
  | 'string[]'
  | { readonly oneOf: readonly string[] };

export const OVERRIDABLE_SETTINGS: Readonly<Record<string, OverridableSettingType>> = {
  bookingWizard: 'boolean',
  bookingWizardAccumulatedStorageRate: 'boolean',
  bookingWizardConfirmNoCoverage: 'boolean',
  bookingWizardCoverageDefault: { oneOf: ['none', 'basic'] },
  bookingWizardCoverageSkipLink: 'boolean',
  bookingWizardFreeCancellationBanner: 'boolean',
  bookingWizardInsuranceStep: 'boolean',
  bookingWizardPremiumInsuranceEnabled: 'boolean',
  bookingWizardServiceFeeNote: 'boolean',
  dropoffCoverOffer: 'boolean',
  dropoffWizard: 'boolean',
  dropoffWizardOrderSummaryOnConfirm: 'boolean',
  // Validated again by `FeatureResolver.dropoffCoverTiers` against the tiers that exist.
  dropoffCoverTiers: 'string[]',
  externalReviewMinRating: 'number',
  findShopTimePrompt: 'boolean',
  freeCancellationMoreInfoPinnedDeadline: 'boolean',
  guestDropoffFirstStepHint: 'boolean',
  isServiceFeeFootnoteEnabled: 'boolean',
  pendingActivationEducationEnabled: 'boolean',
  // The real type is `PickupReviewFlow` (interfaces.ts) — a closed union, so validate against it.
  pickupReviewFlow: { oneOf: ['none', 'in-pickup-modal', 'post-validation'] },
  // The real type is `RecommendedBadgeVariant` (interfaces.ts) — a closed union, so validate against it.
  recommendedBadgeVariant: { oneOf: ['superhero', 'free-cancellation', 'recommended'] },
  serviceFeeFootnoteCities: 'string[]',
  storeReviewMinRating: 'number',
  walkInDropoffWizard: 'boolean',
};

/**
 * The setting that enables the URL override mechanism in production.
 *
 * **Never overridable itself.** If it were, `?lh_ft_urlSettingOverridesEnabled=true` would switch the
 * production kill switch on in the same request it is supposed to be blocked by — the gate has to be
 * something only a stored row can move.
 */
export const URL_SETTING_OVERRIDES_ENABLED_SETTING = 'urlSettingOverridesEnabled';

/** A setting override parsed off the URL, still uncoerced. */
export interface RawUrlSettingOverride {
  /** The setting name, with the `lh_ft_` prefix stripped. */
  name: string;
  /** The raw query-param value — always a string at this point. */
  rawValue: string;
}

/**
 * Outcome of coercing one raw param value.
 *
 * Discriminated on a **string**, not a boolean. `tsconfig.base.json` sets no `strict`, so when an app
 * compiles these lib sources `strictNullChecks` is off and a `{ ok: true } | { ok: false }` union stops
 * narrowing — the lib's own build (which sets `strict: true`) passes while every app build fails. A
 * string discriminant narrows either way.
 */
export type SettingCoercion =
  | { kind: 'coerced'; value: unknown }
  | { kind: 'rejected'; reason: UrlSettingOverrideRejection };

/** Why a parsed override was not applied. Used to warn with something actionable. */
export type UrlSettingOverrideRejection = 'not-allow-listed' | 'unknown-setting' | 'uncoercible';

export interface UrlSettingOverrideResult {
  /** The overrides that were applied, keyed by setting name, coerced to the setting's own type. */
  applied: Record<string, unknown>;
  /** The overrides that were dropped, with the reason — so callers can warn per key. */
  rejected: { name: string; rawValue: string; reason: UrlSettingOverrideRejection }[];
}

/**
 * Extracts the `lh_ft_*` params from a query-param object, in URL order.
 *
 * Returns them raw: parsing is separated from applying because the params are read at app bootstrap
 * (before settings exist) while coercion needs the assembled settings to know each target type.
 */
export function parseUrlSettingOverrideParams(params: Record<string, string> | undefined): RawUrlSettingOverride[] {
  if (!params) {
    return [];
  }

  const overrides: RawUrlSettingOverride[] = [];

  for (const param of Object.keys(params)) {
    if (!param.startsWith(URL_SETTING_OVERRIDE_PREFIX) || param === URL_SETTING_OVERRIDE_CLEAR_PARAM) {
      continue;
    }

    const name = param.substring(URL_SETTING_OVERRIDE_PREFIX.length);
    if (!name) {
      continue;
    }

    overrides.push({ name, rawValue: String(params[param] ?? '') });
  }

  return overrides;
}

/** Whether the given query params ask for all overrides to be cleared (`?lh_ft_clear=1`). */
export function hasUrlSettingOverrideClearParam(params: Record<string, string> | undefined): boolean {
  if (!params) {
    return false;
  }
  const value = params[URL_SETTING_OVERRIDE_CLEAR_PARAM];
  return value !== undefined && value !== 'false' && value !== '0';
}

/**
 * Coerces a raw query-param string to an allow-listed setting's declared type.
 *
 * This exists because {@link AppConfig.override} **discards** a value whose `typeof` differs from the
 * one it is replacing. Query params are always strings, so `?lh_ft_bookingWizard=false` would arrive as
 * the string `'false'` — which is truthy — and either be dropped or resolve to the *opposite* of what
 * was typed. Both failure modes are silent, which is the worst possible outcome mid-demo.
 *
 * Coercion targets the type the allow-list **declares**, not the type of whatever happens to be in
 * settings right now — see {@link OVERRIDABLE_SETTINGS} for why that distinction is load-bearing.
 *
 * @param rawValue The raw query-param string.
 * @param type The declared type of the target setting.
 * @returns The coerced value, or a rejection reason when the string can't be that type.
 */
export function coerceToSettingType(rawValue: string, type: OverridableSettingType): SettingCoercion {
  if (typeof type === 'object') {
    const value = rawValue.trim();
    return type.oneOf.includes(value) ? { kind: 'coerced', value } : { kind: 'rejected', reason: 'uncoercible' };
  }

  switch (type) {
    case 'string[]': {
      // An empty param means an empty list, not `['']` — `?lh_ft_serviceFeeFootnoteCities=` is how the
      // allowlist is cleared. A single value with no comma is still a one-element list.
      const trimmed = rawValue.trim();
      const value = trimmed === '' ? [] : trimmed.split(',').map((entry) => entry.trim());
      return { kind: 'coerced', value };
    }

    case 'boolean': {
      const normalized = rawValue.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') {
        return { kind: 'coerced', value: true };
      }
      if (normalized === 'false' || normalized === '0') {
        return { kind: 'coerced', value: false };
      }
      // Anything else is a typo, not a boolean. Rejecting beats guessing: JS truthiness would read
      // `?lh_ft_bookingWizard=off` as `true`, i.e. the opposite of the evident intent.
      return { kind: 'rejected', reason: 'uncoercible' };
    }

    case 'number': {
      const value = Number(rawValue.trim());
      if (rawValue.trim() === '' || Number.isNaN(value)) {
        return { kind: 'rejected', reason: 'uncoercible' };
      }
      return { kind: 'coerced', value };
    }

    case 'string': {
      return { kind: 'coerced', value: rawValue };
    }

    default: {
      // Unreachable through the allow-list, but the switch must not fall off the end: returning
      // `undefined` here let a caller read `.kind` off nothing and throw during app bootstrap.
      return { kind: 'rejected', reason: 'uncoercible' };
    }
  }
}

/**
 * Resolves raw URL overrides against the assembled settings — the allow-list check plus coercion.
 *
 * Pure, and separate from the service, so the precedence rules are unit-testable without Angular,
 * storage or an HTTP stub.
 *
 * @param overrides The raw overrides parsed off the URL.
 * @param settings The assembled settings (server payload merged over app defaults) that the overrides
 *   sit on top of, and whose value types drive coercion.
 */
export function resolveUrlSettingOverrides(
  overrides: RawUrlSettingOverride[],
  settings: SharedAppSettings,
): UrlSettingOverrideResult {
  const result: UrlSettingOverrideResult = { applied: {}, rejected: [] };
  const allowList = OVERRIDABLE_SETTINGS;

  for (const { name, rawValue } of overrides) {
    // Own-property check, not `allowList[name]`: a plain object resolves `constructor` / `toString` /
    // `valueOf` through `Object.prototype`, so those names came back truthy, skipped this guard and
    // reached coercion as a non-type. (The earlier `Set.has()` was safe; converting the allow-list to
    // a keyed map reintroduced the hazard.)
    const isAllowListed = Object.prototype.hasOwnProperty.call(allowList, name);
    const declaredType = isAllowListed ? allowList[name] : undefined;

    if (!declaredType) {
      // An unknown name and a known-but-not-allow-listed one are worth telling apart: the first is
      // almost always a typo the caller can fix, the second is a deliberate policy decision.
      const reason: UrlSettingOverrideRejection = Object.prototype.hasOwnProperty.call(settings, name)
        ? 'not-allow-listed'
        : 'unknown-setting';
      result.rejected.push({ name, rawValue, reason });
      continue;
    }

    const coerced = coerceToSettingType(rawValue, declaredType);
    if (coerced.kind === 'rejected') {
      result.rejected.push({ name, rawValue, reason: coerced.reason });
      continue;
    }

    result.applied[name] = coerced.value;
  }

  return result;
}

/** Human-readable explanation for a rejected override, for the console warning. */
export function describeUrlSettingOverrideRejection(rejection: UrlSettingOverrideResult['rejected'][number]): string {
  const param = `${URL_SETTING_OVERRIDE_PREFIX}${rejection.name}=${rejection.rawValue}`;

  switch (rejection.reason) {
    case 'unknown-setting': {
      return `Ignoring ?${param} — '${rejection.name}' is not an app setting (typo?).`;
    }
    case 'not-allow-listed': {
      return `Ignoring ?${param} — '${rejection.name}' is not overridable from the URL.`;
    }
    case 'uncoercible': {
      return `Ignoring ?${param} — '${rejection.rawValue}' is not a valid value for '${rejection.name}'.`;
    }
  }
}
